/* Size guide widget (Andre, 2026-08-30).
 *
 * Takes a foot length, names the size, and marks the row in the chart.
 *
 * MATCHING. Each row's `cm` is that size's MAXIMUM foot length, so the match is
 * "the first row whose maximum is at least your measurement". The rows arrive
 * already ordered from the shop metafield; nothing here re-derives a boundary,
 * because the boundaries are the factory lasts minus 1.3 cm and hand-tuning one
 * is what made the old chart run big.
 *
 * BOTH UNITS, ALWAYS. Whatever unit the customer types in, the answer quotes
 * the measurement back in centimetres AND inches (Andre, 2026-07-16). The same
 * rule customer service follows on a ticket.
 *
 * WHY IT DOES NOT AUTO-SCROLL. The answer appears in the widget immediately and
 * the row is marked immediately, but the page does not jump: a scroll on every
 * keystroke fights the person still typing. "Show me in the chart" moves them
 * when they ask, offset for the fixed nav, which does not reserve its own space.
 */
(function () {
  var root = document.querySelector('[data-psg]');
  if (!root) return;

  var dataEl = root.querySelector('[data-psg-data]');
  if (!dataEl) return;

  var data;
  try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
  if (!data) return;

  var copy = data.copy || {};
  /* Language-aware words that are not settings (2026-09-15): snippets/pulse-size-i18n.liquid, rendered inside this root. */
  var i18n = {};
  try { var i18nEl = root.querySelector('[data-pulse-size-i18n]'); if (i18nEl) i18n = JSON.parse(i18nEl.textContent) || {}; } catch (e) {}
  var input = root.querySelector('[data-psg-input]');
  var result = root.querySelector('[data-psg-result]');
  if (!input || !result) return;

  /* Inches, not centimetres (Andre, 2026-09-02: "I would like the size guide to
     default to women's inch"). */
  /* THE STARTING UNIT IS WHICHEVER BUTTON THE MARKUP LIGHTS, never a constant
     here. Two markups share this file: the size-guide page (in pressed) and the
     PDP drawer (cm pressed). When the default above moved to inches for the
     page on 2026-09-02, the drawer kept its lit cm button and this script kept
     assuming inches, so "24.3" typed under a lit cm read as 24.3 in = 61.7 cm and
     was rejected as "not a foot length" (Andre, 2026-09-03: "when you enter a
     shoe size in the size chart, the functionality doesn't work"). The button
     the customer can see is the only honest source. */
  var unitOn = root.querySelector('[data-psg-unit].is-on');
  var unit = unitOn ? unitOn.getAttribute('data-psg-unit') : 'in';
  /* The placeholder follows the lit unit. The page lit inches while the
     input still hinted "e.g. 24.3", a centimetre length (audit LQ-26,
     2026-09-03). Both hints ride on the input as data-ph-cm / data-ph-in. */
  function placeholder() {
    var ph = input.getAttribute('data-ph-' + unit);
    if (ph) input.placeholder = ph;
  }
  placeholder();
  /* An explicit tap on cm / in narrows the foot-length column straight away,
     with nothing entered (Andre, 2026-09-01: "if a customer just wants to look
     at the size chart and they hit inches, nothing changes"). update() bails on
     an empty field before it reaches columns(), and hint() actively reset the
     columns to "show both", so the button did nothing until a number was typed.

     The flag is what keeps the DEFAULT intact: on load nothing has been chosen,
     so the chart is still the full reference with both units showing. Only a
     press sets it. The women's / men's narrowing is unchanged and still waits
     for a measurement, because a scale is an inference from what you tell us
     and a unit is a direct instruction. */
  var unitPicked = false;
  var range = root.getAttribute('data-default-range') || 'new';
  /* Seeded from window.PulseSizeScale by paintGender() below; this initial
     value only covers the instants before that call. */
  var gender = 'women';

  /* A foot outside this is a typo or the wrong unit, not a foot. Checked before
     anything else so "9.5" typed while cm is selected is caught rather than
     silently answered as the smallest size. */
  var SANE_MIN = 15;
  var SANE_MAX = 40;
  /* Below this the shoe would be visibly too big whatever the chart's top row
     says, so say so rather than sell a size that will come back. */
  var SMALLEST = 20;

  function rowsFor(key) {
    var r = data[key];
    return Array.isArray(r) ? r : [];
  }

  function num(v) {
    var n = parseFloat(v);
    return isNaN(n) ? null : n;
  }

  function match(cm) {
    var rows = rowsFor(range);
    if (!rows.length) return null;
    for (var i = 0; i < rows.length; i++) {
      var max = num(rows[i].cm);
      if (max !== null && cm <= max) return rows[i];
    }
    return null;
  }

  function fill(tpl, map) {
    return String(tpl || '').replace(/\[(\w+)\]/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : whole;
    });
  }

  function clearRows() {
    var marked = root.querySelectorAll('tr.is-match');
    for (var i = 0; i < marked.length; i++) marked[i].classList.remove('is-match');
    /* The PDP drawer's Select buttons sit at tabindex -1 so twenty hidden ones
       stay out of the tab order; only the matched row's is reachable. */
    var picks = root.querySelectorAll('.pulse-sizedrawer__pick');
    for (var p = 0; p < picks.length; p++) picks[p].tabIndex = -1;
  }

  function markRow(row) {
    clearRows();
    if (!row) return null;
    var panel = root.querySelector('[data-psg-table="' + range + '"]');
    if (!panel) return null;
    var trs = panel.querySelectorAll('[data-psg-row]');
    for (var i = 0; i < trs.length; i++) {
      if (trs[i].getAttribute('data-usw') === String(row.usw) &&
          trs[i].getAttribute('data-usm') === String(row.usm)) {
        trs[i].classList.add('is-match');
        var pick = trs[i].querySelector('.pulse-sizedrawer__pick');
        if (pick) pick.tabIndex = 0;
        return trs[i];
      }
    }
    return null;
  }

  /* THE CHART IS ALWAYS NARROWED TO THE CONTROLS (Andre, 2026-09-02: "default to
     women's inch, and when defaulted to that you will only see U.S. women and
     inch in the size guide table below. If they change men's inch, it'll switch
     to men's inch in the table below").

     This REVERSES the 2026-08-31 rule that the chart stayed the full reference
     until it had been told something. The scale and the unit are now read as
     instructions from the moment the page loads, so `unitPicked` no longer gates
     anything and the defaults (inches, women's) are what the table shows.
     UK and EU are not gendered and stay in every state. */
  function columns(unitKey, scaleKey) {
    root.setAttribute('data-psg-cols', unitKey || unit);
    root.setAttribute('data-psg-scale', scaleKey || gender);
  }

  /* THE ANSWER IS ANNOUNCED (2026-09-13). The one-question-at-a-time flows on the size guide page and in
     the PDP drawer (assets/pulse-size-reco.js, assets/pulse-size-reco-drawer.js) build their women's or
     men's step and their answer card from this, rather than re-deriving the match. `data-psg-state` on the
     root lets CSS hide the one-line answer where a card replaces it; warnings still show. */
  function announce(state, row, cm) {
    root.setAttribute('data-psg-state', state);
    root.dispatchEvent(new CustomEvent('psg:answer', { detail: { state: state, row: row || null, cm: cm || null } }));
  }

  function message(text) {
    columns(unit, gender);
    clearRows();
    result.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'psg__warn';
    p.textContent = text;
    result.appendChild(p);
    announce('warn');
  }

  function hint() {
    columns(unit, gender);
    clearRows();
    result.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'psg__hint';
    p.textContent = copy.hint || '';
    result.appendChild(p);
    announce('hint');
  }

  /* Kept so the hint survives a re-render: read once from the markup Liquid
     produced rather than duplicating the copy here. */
  var hintEl = root.querySelector('[data-psg-hint]');
  copy.hint = hintEl ? hintEl.textContent : '';

  function chip(label, value, key) {
    var li = document.createElement('li');
    li.className = 'psg__size' + (key ? ' psg__size--' + key : '');
    var b = document.createElement('b');
    b.textContent = label;
    li.appendChild(b);
    li.appendChild(document.createTextNode(value));
    return li;
  }

  function answer(cm, row, tr) {
    result.innerHTML = '';

    var p = document.createElement('p');
    p.className = 'psg__answer';
    /* THE UNIT THEY TYPED LEADS THE SENTENCE (Andre, 2026-08-31 23:1x: "if they
       enter inches, it says you're 10 in (cm)"). Reading back "25.4 cm (10.00
       in)" to someone who typed 10 makes them hunt for their own number.

       OPT-IN VIA DATA, so this is not a change to the size-guide page. A blob
       that carries `answerIn` uses it while the inch button is selected;
       one that does not -- the page's -- falls back to `answer` and behaves
       exactly as before. */
    var template = (unit === 'in' && copy.answerIn) ? copy.answerIn : copy.answer;
    p.textContent = fill(template, {
      /* toFixed(1), not a bare round: 22 must read "22.0 cm" to match the column
         it is being compared against. */
      cm: cm.toFixed(1),
      in: (cm / 2.54).toFixed(2),
      /* [in1] is the same figure at one decimal, for a template that LEADS with
         inches: someone who typed 9.6 should be read back 9.6, not 9.61.
         [uk] and [eu] let one sentence carry the whole answer instead of a
         sentence plus a separate row of chips saying it again. All three are
         additive -- a template that does not use them is unaffected, which is
         what keeps the size-guide page's own wording working. */
      in1: (cm / 2.54).toFixed(1),
      uk: String(row.uk),
      eu: String(row.eu),
      /* The sentence names the size in whichever scale the toggle is showing, so
         the answer and the visible column can never disagree. */
      scale: gender === 'men' ? (copy.scaleMen || "men's") : (copy.scaleWomen || "women's"),
      size: gender === 'men' ? String(row.usm) : String(row.usw)
    });
    /* ONE SENTENCE ON THE SIZE GUIDE PAGE (Andre, 2026-09-11: "We've got results
       twice ... use some common sense"). The page carries data-psg-answer="line":
       the sentence names the scale they picked plus UK and EU, and nothing follows
       it -- no chips repeating the same sizes, no "Show me in the chart" (the row is
       already lit). The PDP size drawer does not carry the attribute and keeps the
       sentence + chips + jump exactly as before. Lead words come from the page's own
       answer template, up to [scale], so an edit to that setting still lands. */
    if (root.getAttribute('data-psg-answer') === 'line') {
      var lead = String(template || '').split('[scale]')[0].replace(/\b(an?)\s*$/i, '');
      p.textContent = fill(lead, { cm: cm.toFixed(1), in: (cm / 2.54).toFixed(2), in1: (cm / 2.54).toFixed(1) });
      var grp = document.createElement('span');
      grp.className = 'reco__sizes';
      var big = document.createElement('b');
      big.textContent = fill(i18n.answer_bold || '[scale] US [size]', {
        scale: gender === 'men' ? (copy.scaleMen || "men's") : (copy.scaleWomen || "women's"),
        size: String(gender === 'men' ? row.usm : row.usw)
      });
      grp.appendChild(big);
      grp.appendChild(document.createTextNode(' \u00b7 UK ' + row.uk + ' \u00b7 EU ' + row.eu + '.'));
      p.appendChild(grp);
      result.appendChild(p);
      return;
    }
    result.appendChild(p);

    /* ONLY the scale they picked (Andre, 2026-08-31: "it only gives them the
       male/female answer"). Showing both US numbers is the thing that lets
       someone read the wrong one. UK and EU are not gendered, so they stay. */
    var ul = document.createElement('ul');
    ul.className = 'psg__sizes';
    if (gender === 'men') {
      ul.appendChild(chip(copy.usm || 'US Men', row.usm, 'us'));
    } else {
      ul.appendChild(chip(copy.usw || 'US Women', row.usw, 'us'));
    }
    ul.appendChild(chip(copy.uk || 'UK', row.uk));
    ul.appendChild(chip(copy.eu || 'EU', row.eu));
    result.appendChild(ul);

    if (tr) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'psg__jump';
      btn.textContent = copy.showInChart || 'Show me in the chart';
      btn.addEventListener('click', function () {
        /* .announce and .pulse-nav are fixed and reserve no space, so a plain
           scrollIntoView parks the row under the header. */
        var nav = document.querySelector('.pulse-nav');
        var offset = nav ? nav.getBoundingClientRect().bottom + 16 : 120;
        var y = tr.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      });
      result.appendChild(btn);
    }
  }

  function update() {
    var raw = input.value.replace(',', '.').trim();
    if (!raw) { hint(); return; }

    var v = num(raw);
    if (v === null || v <= 0) { hint(); return; }

    /* MATCH AT THE CHART'S OWN PRECISION. The chart is authored in tenths of a
       centimetre, so the comparison happens in tenths too. Without this, 9.57 in
       converts to 24.3078 cm, spills past the 24.3 row and highlights the row
       BELOW the one whose printed inch range (9.45 to 9.57) contains it -- while
       the answer line, which rounds for display, still reads 24.3 cm. Display
       precision and match precision have to be the same precision. */
    var cm = Math.round((unit === 'in' ? v * 2.54 : v) * 10) / 10;

    if (cm < SANE_MIN || cm > SANE_MAX) { message(copy.unclear); return; }
    if (cm < SMALLEST) { message(copy.tooSmall); return; }

    var row = match(cm);
    if (!row) { message(copy.tooBig); return; }

    columns(unit, gender);
    answer(cm, row, markRow(row));
    announce('match', row, cm);
    if (window.PulseSizeTrack) {
      var t = PulseSizeTrack, ctx = t.context(), where = ctx.product ? 'product page' : 'size guide';
      var typed = unit === 'in' ? (cm / 2.54).toFixed(2) + ' in' : cm.toFixed(1) + ' cm';
      t.record(where + '-measure', {
        surface: where, method: 'measure my foot', market: ctx.market, product: ctx.product,
        foot_length: typed, foot_cm: cm, scale: gender, status: 'match', pulse_size: t.pulseSize(row, gender),
        pulse_us_women: row.usw, pulse_us_men: row.usm, pulse_uk: row.uk, pulse_eu: row.eu,
        summary: 'Foot ' + typed + ' -> Pulse ' + t.pulseSize(row, gender) + ' (' + where + (ctx.product ? ': ' + ctx.product : '') + ')'
      });
    }
  }

  input.addEventListener('input', update);

  var unitBtns = root.querySelectorAll('[data-psg-unit]');
  for (var u = 0; u < unitBtns.length; u++) {
    unitBtns[u].addEventListener('click', function () {
      unit = this.getAttribute('data-psg-unit');
      placeholder();
      unitPicked = true;
      for (var i = 0; i < unitBtns.length; i++) {
        var on = unitBtns[i] === this;
        unitBtns[i].classList.toggle('is-on', on);
        unitBtns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      update();
    });
  }

  var rangeBtns = root.querySelectorAll('[data-psg-range]');
  for (var r = 0; r < rangeBtns.length; r++) {
    rangeBtns[r].addEventListener('click', function () {
      range = this.getAttribute('data-psg-range');
      for (var i = 0; i < rangeBtns.length; i++) {
        var on = rangeBtns[i] === this;
        rangeBtns[i].classList.toggle('is-on', on);
        rangeBtns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      var panels = root.querySelectorAll('[data-psg-panel]');
      for (var p = 0; p < panels.length; p++) {
        var show = panels[p].getAttribute('data-psg-panel') === range;
        panels[p].classList.toggle('is-hidden', !show);
        if (show) { panels[p].removeAttribute('hidden'); } else { panels[p].setAttribute('hidden', ''); }
      }
      /* The two ranges chart differently, so the same measurement can land on a
         different size. Re-run rather than leave a stale answer on screen. */
      update();
    });
  }

  /* WOMEN'S / MEN'S. Picking a scale does not narrow the chart on its own -- the
     narrowing happens in update(), once there is a measurement to narrow FOR.

     The choice is NOT this widget's to keep. window.PulseSizeScale
     (snippets/pulse-size-scale.liquid) holds the one preference the PDP, the
     collection wall and the cart all read, so a customer who chose Men's on a
     product page finds the guide already on men's, and a scale picked here
     follows them back out. This file used to keep a page-local `gender` that
     agreed with the rest of the site only by sharing its default -- which is
     agreement by luck, and it broke the moment anything was clicked.

     The click handler ONLY calls set(); the repaint runs from the subscription,
     so the two paths cannot re-enter each other. */
  var genderBtns = root.querySelectorAll('[data-psg-gender-btn]');

  function paintGender(g, rerender) {
    gender = g;
    for (var i = 0; i < genderBtns.length; i++) {
      var on = genderBtns[i].getAttribute('data-psg-gender-btn') === g;
      genderBtns[i].classList.toggle('is-on', on);
      genderBtns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    /* The TABLE follows the scale immediately, on load and on every switch
       (Andre, 2026-09-02): picking Men's swaps the US column without needing a
       measurement first. */
    columns(unit, gender);
    /* update() itself is still deliberately NOT called on load: on an empty
       field it replaces the widget's resting copy with the type-something hint,
       which would be a visible change for a customer who has not touched it. */
    if (rerender) update();
  }

  paintGender(window.PulseSizeScale.gender(), false);
  window.PulseSizeScale.onChange(function (v) {
    paintGender(v === 'm' ? 'men' : 'women', true);
  });

  for (var bi = 0; bi < genderBtns.length; bi++) {
    genderBtns[bi].addEventListener('click', function () {
      window.PulseSizeScale.set(this.getAttribute('data-psg-gender-btn'));
    });
  }

})();
